import type { Messages } from "../../../core/api";

// Pure value logic of the RN-platform physician editor. The answer is the
// `RNPlatformPatientPhysicianDetails` object mobile stores after the physician search (name,
// address, npi, phoneNumbers { number, institution }[], faxNumbers string[]).
// Only `import type` from core/api so the module loads in node tests.

export type PhysicianDetails = Messages["RNPlatformPatientPhysicianDetails"];
type PhysicianPhone = PhysicianDetails["phoneNumbers"][number];

/** Form state: every field a string, blank rows allowed while typing. */
export interface PhysicianDraft {
  address: string;
  faxNumbers: string[];
  name: string;
  npi: string;
  phoneNumbers: PhysicianPhone[];
}

export interface StoredPhysician {
  draft: PhysicianDraft;
  /** false when a stored value exists that is not a physician object (kept unless edited) */
  isReadable: boolean;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : typeof value === "number" ? String(value) : "";
}

const EMPTY_DRAFT: PhysicianDraft = {
  address: "",
  faxNumbers: [],
  name: "",
  npi: "",
  phoneNumbers: [],
};

export function readPhysicianAnswer(answer: unknown): StoredPhysician {
  if (answer === null || answer === undefined || answer === "") {
    return { draft: EMPTY_DRAFT, isReadable: true };
  }
  if (typeof answer !== "object" || Array.isArray(answer)) {
    return { draft: EMPTY_DRAFT, isReadable: false };
  }
  const record = answer as Record<string, unknown>;
  const phones = Array.isArray(record.phoneNumbers) ? (record.phoneNumbers as unknown[]) : [];
  const faxes = Array.isArray(record.faxNumbers) ? (record.faxNumbers as unknown[]) : [];
  return {
    draft: {
      address: text(record.address),
      faxNumbers: faxes.map(text),
      name: text(record.name),
      npi: text(record.npi),
      phoneNumbers: phones.map((phone) => {
        const item =
          typeof phone === "object" && phone !== null ? (phone as Record<string, unknown>) : {};
        return { number: text(item.number), institution: text(item.institution) };
      }),
    },
    isReadable: true,
  };
}

function isBlankPhone(phone: PhysicianPhone): boolean {
  return phone.number.trim() === "" && phone.institution.trim() === "";
}

function isEmptyDraft(draft: PhysicianDraft): boolean {
  return (
    draft.name.trim() === "" &&
    draft.address.trim() === "" &&
    draft.npi.trim() === "" &&
    draft.phoneNumbers.every(isBlankPhone) &&
    draft.faxNumbers.every((fax) => fax.trim() === "")
  );
}

/** Why the draft cannot be saved, or null. An all-empty form is valid (it removes the answer). */
export function physicianDraftProblem(draft: PhysicianDraft): string | null {
  if (isEmptyDraft(draft)) {
    return null;
  }
  if (draft.name.trim() === "") {
    return "Enter the physician's name.";
  }
  if (draft.phoneNumbers.some((phone) => !isBlankPhone(phone) && phone.number.trim() === "")) {
    return "Enter the phone number or remove the row.";
  }
  return null;
}

/**
 * The answer to save: trimmed fields, blank phone / fax rows dropped, a blank NPI as "" (not null:
 * some templates print the NPI as is, so null would print "NPI: null"). An all-empty form saves
 * null, which deletes the answer.
 */
export function physicianAnswerFromDraft(draft: PhysicianDraft): PhysicianDetails | null {
  if (isEmptyDraft(draft)) {
    return null;
  }
  return {
    name: draft.name.trim(),
    address: draft.address.trim(),
    npi: draft.npi.trim(),
    phoneNumbers: draft.phoneNumbers
      .filter((phone) => !isBlankPhone(phone))
      .map((phone) => ({ number: phone.number.trim(), institution: phone.institution.trim() })),
    faxNumbers: draft.faxNumbers.map((fax) => fax.trim()).filter((fax) => fax !== ""),
  };
}
